/* ---------------------------------------------------------------------------
   PCDG — prestressed concrete deck girder (precast I-girder), simple span,
   interior or exterior.

   THE FAMILY WITH A CLIENT WAITING. A simply supported I-girder of 20 to 45 m
   is a prestressed one, which is how FWO describes the stock on the BOT motorway
   in Proposal 2 of 7 September 2026 — and that proposal prices a load rating per
   bridge. Neither finished family covered it: RCFS is a flat slab and RCDG is a
   reinforced T-beam.

   WHAT IT ADDS OVER RCDG, and none of these is a bigger version of the same
   thing:

   1. TWO SECTIONS, NOT ONE. The girder acts alone under its own weight and the
      wet slab, and compositely with the hardened slab under everything after.
      So every stress is computed on whichever section was carrying that load
      when it arrived, and the two have different centroids, different second
      moments and different section moduli. `pcdgSectionProperties` returns both.

   2. SERVICEABILITY IS A RATING, NOT A CHECK. Three of the seven ratings are
      allowable-stress limits on concrete fibres — compression under all loads,
      compression under live load, and tension at the soffit — and on the
      reference bridge the TENSION one governs at 1.25 against the strength
      rating's 1.32. A transcription that rated only strength would report this
      bridge 5% stronger than the workbook does.

   3. THE PRESTRESS IS A LOAD. It enters the stress table as its own row, with
      the axial term and the eccentric term at each fibre, and it is subtracted
      from the capacity rather than added to it — which is why `Check.dead`
      carries the combined dead-plus-prestress stress here and a bending moment
      everywhere else.

   4. THE NEUTRAL AXIS IS CIRCULAR AND THE WORKBOOK HAND-CRANKS IT. Correction
      C4: the averaged beta depends on c, and c depends on beta, so the user
      retypes the cell until the two agree. The reference workbook ships NOT
      QUITE CONVERGED — 231.2 typed against 231.165 computed. Solved here by
      iteration to a tolerance; see `pcdgNeutralAxis`.

   ── WHICH WORKBOOK IS THE REFERENCE, AND WHY IT IS THE EXTERIOR ONE ──

   Every other family in this library is checked against its interior sheet. This
   one is checked against `EXTERIOR PCDG SIMPLE`, because the two sheets do not
   agree about the girder they are both describing: the SAME girder has
   Iz = 0.213746167860755 m4 on the exterior sheet and 0.213581121893672 on the
   interior one. The interior sheet's second-moment column reaches one row down
   for every term in the upper flange — correction C16 — and the exterior sheet
   does not. So the exterior workbook is reproduced here to full double
   precision, every value; the interior one is reproduced except where C16 and C4
   say otherwise, and the suite asserts both numbers.

   ── FOUR THINGS THE DEPARTMENT SHOULD BE ASKED ABOUT ──

   C16 is a slip with an obviously intended form elsewhere in the same program,
   so it is corrected. The other three are NOT applied, are reproduced exactly,
   and are on the rating report: the capacity-reduction factor Φ is an input cell
   that no formula reads (C17); the composite section takes the deck slab at its
   gross area with no modular-ratio transformation, which overstates the
   composite Iz by 7% on the reference bridge (C18); and the assumed prestress
   loss is three per cent of the WORKING FORCE IN KILONEWTONS read as a stress in
   megapascals (C19). The last is the one to raise first — it sets the tendon
   area, which sets the moment capacity, and it is correct for this bridge only
   by arithmetic coincidence.
   --------------------------------------------------------------------------- */

import {
  girderDistribution,
  liveLoadDemand,
  type Distribution,
  type LiveLoadDemand,
} from './demand.ts';
import type { RatingLevel, StandardsProfile } from './profile.ts';
import { allVehicles, rate, type Check, type RatingResult } from './rating.ts';
import type { GirderPosition } from './rcdg.ts';
import type { VehicleId } from './vehicles.ts';

/* --------------------------- the section --------------------------- */

/**
 * The precast I-girder, as `LAYOUT!E43:F52` dimensions it. Metres throughout.
 *
 * A FIXED SEVEN-PART SHAPE with variable dimensions, which is what the workbook
 * tabulates and is not a general section builder. The reference sheet calls it
 * "Standard PCDC Type V 35m". Everything below the flange widths is derived the
 * way the sheet derives it — see `girderParts`.
 */
export interface PcdgGirderSection {
  /** Overall girder depth, excluding the deck slab. `LAYOUT!E43`. */
  height: number;
  /** Deck slab thickness. `LAYOUT!F45`. */
  slabThickness: number;
  /** Top flange full width. `LAYOUT!E46`. */
  upperFlangeWidth: number;
  /** Top flange thickness at the tip. `LAYOUT!F46`. */
  upperFlangeThickness: number;
  /** Depth of the taper under the top flange. `LAYOUT!F47`. */
  taperDepth: number;
  /** The square fillet between taper and web. `LAYOUT!E49` = `F49`. */
  fillet: number;
  /** Web width bw. `LAYOUT!E50`. */
  webWidth: number;
  /** The bottom-flange fillet, square. `LAYOUT!E51` = `F51`. */
  lowerFillet: number;
  /** Bottom flange width. `LAYOUT!E52`. */
  lowerFlangeWidth: number;
  /** Bottom flange thickness. `LAYOUT!F52`. */
  lowerFlangeThickness: number;
}

/**
 * One rectangle or pair of triangles in the tabulation.
 *
 * `area` IS `b x h` FOR EVERY PART, including the ones whose second moment is
 * written `2 b h^3 / 36`. That is the workbook's own idealisation and it is not
 * internally consistent — the "2 rectangles" term at the flange step takes
 * `2 b h^3 / 12` against an area of `b h`. Reproduced rather than tidied: the
 * whole section table is calibrated against reference values as it stands, and
 * rewriting one part's geometry would change a published section property on our
 * own authority. See the header.
 */
interface SectionPart {
  name: string;
  /** Width, m. */
  b: number;
  /** Depth, m. */
  h: number;
  /** Centroid below the TOP OF THE GIRDER, m. Negative for the deck slab. */
  arm: number;
  /** Second moment about the part's own centroid, m4. */
  i0: number;
}

const rect = (b: number, h: number): number => (b * h * h * h) / 12;
const twoTriangles = (b: number, h: number): number => (2 * b * h * h * h) / 36;
const twoRects = (b: number, h: number): number => (2 * b * h * h * h) / 12;

/**
 * The eight parts, deck slab first, in the order `LAYOUT` tabulates them.
 *
 * MEASURED FROM THE TOP OF THE GIRDER, not from the top of the deck. That is the
 * sheet's datum and it is why the slab's arm is negative and why `Ybot` is
 * `girder height − Ytop` even for the composite section, where the true overall
 * depth is 200 mm greater. Every downstream formula shares the datum, so it
 * cancels; changing it would silently move every stress.
 */
function girderParts(section: PcdgGirderSection, slabWidth: number): readonly SectionPart[] {
  const s = section;
  /* `LAYOUT!E47` — what is left of the top flange either side of the web and its
     two fillets, halved. */
  const taperWidth = (s.upperFlangeWidth - s.webWidth - 2 * s.fillet) / 2;
  /* `LAYOUT!F50` — the web is whatever the flanges leave. */
  const webDepth = s.height - s.upperFlangeThickness - s.lowerFlangeThickness;
  const uft = s.upperFlangeThickness;

  return [
    {
      name: 'Deck slab',
      b: slabWidth,
      h: s.slabThickness,
      arm: -(s.slabThickness / 2),
      i0: rect(slabWidth, s.slabThickness),
    },
    {
      name: 'Top flange',
      b: s.upperFlangeWidth,
      h: uft,
      arm: uft / 2,
      i0: rect(s.upperFlangeWidth, uft),
    },
    {
      name: 'Top flange taper',
      b: taperWidth,
      h: s.taperDepth,
      arm: uft + s.taperDepth / 3,
      i0: twoTriangles(taperWidth, s.taperDepth),
    },
    {
      name: 'Top flange step',
      b: s.fillet,
      h: s.taperDepth,
      arm: uft + s.taperDepth / 2,
      i0: twoRects(s.fillet, s.taperDepth),
    },
    {
      name: 'Top fillet',
      b: s.fillet,
      h: s.fillet,
      arm: uft + s.taperDepth + s.fillet / 3,
      i0: twoTriangles(s.fillet, s.fillet),
    },
    {
      name: 'Web',
      b: s.webWidth,
      h: webDepth,
      arm: uft + webDepth / 2,
      i0: rect(s.webWidth, webDepth),
    },
    {
      name: 'Bottom fillet',
      b: s.lowerFillet,
      h: s.lowerFillet,
      arm: s.height - s.lowerFlangeThickness - s.lowerFillet / 3,
      i0: twoTriangles(s.lowerFillet, s.lowerFillet),
    },
    {
      name: 'Bottom flange',
      b: s.lowerFlangeWidth,
      h: s.lowerFlangeThickness,
      arm: s.height - s.lowerFlangeThickness / 2,
      i0: rect(s.lowerFlangeWidth, s.lowerFlangeThickness),
    },
  ];
}

export interface SectionProperties {
  /** Area, m2. */
  area: number;
  /** Centroid below the top of the GIRDER, m. Can exceed the slab's own depth. */
  yTop: number;
  /** Girder height less `yTop`, m — the sheet's `Ybot`, on the same datum. */
  yBottom: number;
  /** Second moment about the centroid, m4. */
  inertia: number;
  /** Section modulus at the top fibre of the girder, m3. */
  sTop: number;
  /** Section modulus at the soffit, m3. */
  sBottom: number;
}

function propertiesOf(
  parts: readonly SectionPart[],
  girderHeight: number,
): SectionProperties {
  let area = 0;
  let firstMoment = 0;
  let secondMoment = 0;
  let own = 0;
  for (const part of parts) {
    const a = part.b * part.h;
    area += a;
    firstMoment += a * part.arm;
    secondMoment += a * part.arm * part.arm;
    own += part.i0;
  }
  const yTop = firstMoment / area;
  const yBottom = girderHeight - yTop;
  const inertia = secondMoment + own - area * yTop * yTop;
  return { area, yTop, yBottom, inertia, sTop: inertia / yTop, sBottom: inertia / yBottom };
}

export interface PcdgSections {
  /** The precast girder alone — carries self weight and the wet slab. */
  girder: SectionProperties;
  /** Girder plus deck — carries everything placed after the slab hardened. */
  composite: SectionProperties;
  /** Depth from the composite centroid to the TOP OF THE DECK, m. `DSC!J6`. */
  compositeToDeckTop: number;
}

/**
 * Both sections, from one set of dimensions.
 *
 * THE DECK IS TAKEN AT ITS GROSS AREA. No modular-ratio transformation is
 * applied, although the deck is 24.2 MPa concrete and the girder 38 MPa on the
 * reference bridge. That is what the workbook does and it is correction C18 —
 * it overstates the composite second moment by 7% there, and it is NOT corrected
 * here because it is a question about what the method means rather than a slip
 * with an intended form elsewhere in the program.
 */
export function pcdgSectionProperties(
  section: PcdgGirderSection,
  slabWidth: number,
): PcdgSections {
  const parts = girderParts(section, slabWidth);
  const girder = propertiesOf(parts.slice(1), section.height);
  const composite = propertiesOf(parts, section.height);
  return {
    girder,
    composite,
    compositeToDeckTop: composite.yTop + section.slabThickness,
  };
}

/* --------------------------- the input --------------------------- */

export interface PcdgPrestress {
  /** Working force after all losses, kN. `GENERAL!G23`. */
  workingForce: number;
  /** Tendon centroid above the soffit at midspan, mm. `GENERAL!G24`. */
  centroidFromSoffit: number;
  /** Ultimate strength of the tendons fu, MPa. `GENERAL!G22`. */
  ultimateStrength: number;
  /** Yield strength of the tendons fy, MPa. `CCMS!H17`. */
  yieldStrength: number;
  /**
   * The assumed-loss fraction, `CCMS!H8`'s 0.03.
   *
   * CORRECTION C19, AND READ IT BEFORE CHANGING A BRIDGE'S PRESTRESS. The sheet
   * computes the assumed loss as this fraction times the WORKING FORCE IN
   * KILONEWTONS and then uses the result as a stress in megapascals. It is
   * dimensionally incoherent: on the reference bridge 0.03 x 5270 gives
   * 158.1 "MPa", which is 11.3% of 0.75 fu and has no stated basis. It is
   * reproduced because it sets the tendon area and therefore every moment
   * capacity in the family, and changing it would move a published rating on
   * this library's own authority.
   */
  lossFraction: number;
  /** Loss of tendon cross-section to corrosion, per cent. `CCMS!H13`. */
  sectionLoss: number;
}

export interface PcdgInput {
  position: GirderPosition;

  /** Span, m. `GENERAL!E13`. */
  span: number;
  /** Girders across the deck. `GENERAL!E15`. */
  girderCount: number;
  /** Girder spacing on centres, m. `GENERAL!E16`. Sets the live-load distribution. */
  girderSpacing: number;
  /**
   * Width of deck this girder carries, m. `LAYOUT!E45`.
   *
   * Equal to the spacing on the interior sheet, where it is a link to it; an
   * independent input on the exterior sheet, where it is 2.57 m against a 2.1 m
   * spacing. It is BOTH the slab's width in the composite section AND the
   * effective flange width the ultimate moment uses (`CCMS!H15`), which is why
   * it is one field and not two.
   */
  slabWidth: number;
  /**
   * Width of sidewalk carried, m. `LAYOUT!E53`. Exterior girders only.
   *
   * Subtracted from the asphalt width and from nothing else: a footway carries
   * no wearing course but the concrete under it is there either way.
   */
  sidewalkWidth?: number | null;

  section: PcdgGirderSection;

  /** Weight of ONE barrier rail, kN/m. `GENERAL!G26`. */
  barrierRail: number;
  /** Diaphragm weight per metre of girder, kN/m. `DFDLLL!H13`. */
  diaphragm: number;
  /** Wearing course thickness, MILLIMETRES. `GENERAL!E18`. */
  wearingCourse: number;
  concreteUnitWeight: number;
  asphaltUnitWeight: number;

  /** Girder concrete f'c, MPa. `GENERAL!G20`. */
  girderStrength: number;
  /** Deck concrete f'c, MPa. `GENERAL!G21`. */
  slabStrength: number;
  /** Reinforcement yield fy, MPa. `GENERAL!G27`. Stirrups only in this family. */
  rebarYield: number;

  prestress: PcdgPrestress;

  /** Stirrup bar diameter, mm. `CCMS!I26`. */
  stirrupDiameter: number;
  /** Stirrup spacing along the span, mm. `CCMS!I28`. */
  stirrupSpacing: number;
  /** Loss of stirrup cross-section to exposure, per cent. `CCMS!I29`. */
  stirrupSectionLoss: number;
  /** Depth of shear cracking into the web, mm. `CCMS!I24`. */
  shearCrackDepth: number;
}

/* --------------------------- dead load --------------------------- */

export interface PcdgDeadLoad {
  /** Girder self weight, kN/m. */
  girder: number;
  /** Deck slab this girder carries, kN/m. */
  slab: number;
  /** Diaphragms, kN/m. */
  diaphragm: number;
  /** This girder's share of the two barrier rails, kN/m. */
  barrier: number;
  /** Asphalt over the width this girder carries, kN/m. */
  overlay: number;
  /**
   * Girder + slab + diaphragms, kN/m — carried by the GIRDER ALONE.
   *
   * The split is the whole reason this family has two sections. Everything in
   * `dead` was on the girder before the slab could work with it; everything in
   * `superimposed` arrived afterwards.
   */
  dead: number;
  /** Barrier + asphalt, kN/m — carried by the COMPOSITE section. */
  superimposed: number;
  /** Midspan moment from `dead`, kN-m. */
  deadMoment: number;
  /** Midspan moment from `superimposed`, kN-m. */
  superimposedMoment: number;
  /** Support shear from `dead`, kN. */
  deadShear: number;
  /** Support shear from `superimposed`, kN. */
  superimposedShear: number;
}

/** Reproduces `DFDLLL!H18:H29`. */
export function pcdgDeadLoad(input: PcdgInput, sections: PcdgSections): PcdgDeadLoad {
  const wu = input.concreteUnitWeight;
  const girder = sections.girder.area * wu;
  const slab = input.slabWidth * input.section.slabThickness * wu;
  const diaphragm = input.diaphragm;
  const barrier = (2 * input.barrierRail) / input.girderCount;
  const overlay =
    (input.wearingCourse / 1000) *
    (input.slabWidth - (input.sidewalkWidth ?? 0)) *
    input.asphaltUnitWeight;

  const dead = girder + slab + diaphragm;
  const superimposed = barrier + overlay;
  const l = input.span;

  return {
    girder,
    slab,
    diaphragm,
    barrier,
    overlay,
    dead,
    superimposed,
    deadMoment: (dead * l * l) / 8,
    superimposedMoment: (superimposed * l * l) / 8,
    deadShear: (dead * l) / 2,
    superimposedShear: (superimposed * l) / 2,
  };
}

/* --------------------------- stresses --------------------------- */

/** One load case at the two fibres that are rated. MPa, tension positive at top. */
export interface FibreStress {
  /** Top of the girder. */
  top: number;
  /** Soffit. */
  bottom: number;
}

export interface PcdgStresses {
  /** Girder self weight and wet slab, on the girder section. `DSC!E13`, `G13`. */
  dead: FibreStress;
  /** Prestress after all losses, on the girder section. `DSC!E14`, `G14`. */
  prestress: FibreStress;
  /** Barrier and asphalt, on the composite section. `DSC!E15`, `G15`. */
  superimposed: FibreStress;
  /** Everything permanent: dead + superimposed + prestress. What a rating subtracts. */
  permanent: FibreStress;
}

/**
 * Stresses from the permanent loads, each on the section that carried it.
 *
 * SIGN CONVENTION, because it decides two of the three serviceability ratings:
 * sagging puts the top fibre in compression and this follows the sheet in
 * calling that POSITIVE, so the soffit's tension comes out negative. The
 * prestress then arrives with the signs reversed — compression at the soffit,
 * a little tension at the top — and the tension rating is a comparison of two
 * negative numbers. Reversing the convention inverts `rfTension` silently.
 */
export function pcdgStresses(
  input: PcdgInput,
  sections: PcdgSections,
  dead: PcdgDeadLoad,
): PcdgStresses {
  const g = sections.girder;
  const c = sections.composite;
  const p = input.prestress.workingForce;
  /* Lever arm of the tendon force about the GIRDER centroid. */
  const arm = g.yBottom - input.prestress.centroidFromSoffit / 1000;

  const deadStress: FibreStress = {
    top: (dead.deadMoment * g.yTop) / g.inertia / 1000,
    bottom: (-dead.deadMoment * g.yBottom) / g.inertia / 1000,
  };
  const prestress: FibreStress = {
    top: (p / g.area - (p * arm * g.yTop) / g.inertia) / 1000,
    bottom: (p / g.area + (p * arm * g.yBottom) / g.inertia) / 1000,
  };
  const superimposed: FibreStress = {
    top: (dead.superimposedMoment * c.yTop) / c.inertia / 1000,
    bottom: (-dead.superimposedMoment * c.yBottom) / c.inertia / 1000,
  };

  return {
    dead: deadStress,
    prestress,
    superimposed,
    permanent: {
      top: deadStress.top + superimposed.top + prestress.top,
      bottom: deadStress.bottom + superimposed.bottom + prestress.bottom,
    },
  };
}

/** Live-load stress at each fibre, on the composite section. `DSC!E16`, `G16`. */
export function liveStress(moment: number, sections: PcdgSections): FibreStress {
  const c = sections.composite;
  return {
    top: (moment * c.yTop) / c.inertia / 1000,
    bottom: (-moment * c.yBottom) / c.inertia / 1000,
  };
}

/* --------------------------- moment capacity --------------------------- */

/** AASHTO's beta1, `CCMS!B42`. */
export function beta1(strength: number): number {
  if (strength > 56) return 0.65;
  if (strength < 28) return 0.85;
  return 0.85 - (0.05 / 7) * (strength - 28);
}

export interface PcdgNeutralAxis {
  /** Depth of the compression block's neutral axis below the deck top, mm. */
  c: number;
  /** Whether the compression zone stays inside the deck slab. */
  withinSlab: boolean;
  /** `c` on the slab-controlled assumption, mm. `CCMS!B44`. */
  cSlab: number;
  /** `c` on the girder-controlled assumption, mm. `CCMS!B46`. Null where unused. */
  cGirder: number | null;
  /** The width-and-strength-averaged beta, `CCMS!F42`. 1 where the slab controls. */
  betaAverage: number;
  /** Iterations the girder-controlled solution needed. Zero where the slab controls. */
  iterations: number;
}

/**
 * Where the compression block ends, iterated rather than retyped.
 *
 * THIS IS CORRECTION C4. Two cases, and the first decides:
 *
 *   SLAB-CONTROLLED. Assume the block stays inside the deck. `c` is then closed
 *   form — nothing depends on beta but the slab's own, which is a constant. If
 *   that `c` is no deeper than the slab, it is the answer and this returns
 *   without iterating. (The reference EXTERIOR girder is this case at 169.5 mm
 *   in a 200 mm slab; the interior one is not, at 205.6 mm.)
 *
 *   GIRDER-CONTROLLED. The block spills into the girder, so part of it is
 *   38 MPa concrete and part 24.2 MPa. The workbook averages the two betas by
 *   area x strength — and that average depends on how far the block has spilled,
 *   which is what we are solving for. The sheet resolves it by making the user
 *   retype `cinput` until it stops moving, and RECORDS NOTHING ABOUT WHETHER
 *   THEY DID: the reference workbook ships with 231.2 typed against 231.165
 *   computed, so it is not converged and reads exactly like it is.
 *
 * Iterated here to 1e-12 mm, which on the reference bridge settles at
 * 231.1629819953219 against the workbook's one-step 231.165274066931 — a
 * difference of 0.002 mm that moves the moment capacity by 0.008 kN-m in 12,007
 * and the rating factor in its sixth decimal. The point is not the size of the
 * difference; it is that the workbook cannot tell you whether it converged.
 */
export function pcdgNeutralAxis(input: PcdgInput, sections: PcdgSections): PcdgNeutralAxis {
  const fu = input.prestress.ultimateStrength;
  const aps = tendonArea(input);
  const d = depthToTendons(input, sections);
  const k = 2 * (1.04 - input.prestress.yieldStrength / fu);
  const beff = input.slabWidth * 1000;
  const ts = input.section.slabThickness * 1000;
  const bw = input.section.webWidth * 1000;
  const bSlab = beta1(input.slabStrength);
  const bGirder = beta1(input.girderStrength);

  const cSlab =
    (aps * fu) / (0.85 * input.slabStrength * bSlab * beff + (k * aps * fu) / d);
  if (cSlab <= ts) {
    return {
      c: cSlab,
      withinSlab: true,
      cSlab,
      cGirder: null,
      betaAverage: bSlab,
      iterations: 0,
    };
  }

  /* The flange force the girder concrete does NOT provide, which is what the
     spilled block has to make up. `CCMS!B46`'s numerator. */
  const flangeSurplus = input.slabStrength * beff - input.girderStrength * bw;

  let c = cSlab;
  let betaAverage = bSlab;
  let iterations = 0;
  for (; iterations < 100; iterations += 1) {
    const areaGirder = c <= ts ? 0 : bw * (c - ts);
    const areaSlab = c > ts ? beff * ts : beff * c;
    const weighted =
      input.slabStrength * areaSlab + input.girderStrength * areaGirder;
    betaAverage =
      weighted === 0
        ? bSlab
        : (input.slabStrength * areaSlab * bSlab +
            input.girderStrength * areaGirder * bGirder) /
          weighted;

    const next =
      (aps * fu - 0.85 * ts * betaAverage * flangeSurplus) /
      (0.85 * input.girderStrength * betaAverage * bw + (k * aps * fu) / d);
    if (Math.abs(next - c) < 1e-12) {
      c = next;
      break;
    }
    c = next;
  }

  return { c, withinSlab: false, cSlab, cGirder: c, betaAverage, iterations };
}

/** Tendon area after section loss, mm2. `CCMS!H12:H14`. See C19 on the loss. */
export function tendonArea(input: PcdgInput): number {
  const p = input.prestress;
  const loss = p.workingForce * p.lossFraction;
  const gross = p.workingForce / ((0.75 * p.ultimateStrength - loss) / 1000);
  return (1 - p.sectionLoss / 100) * gross;
}

/** Tendon centroid below the top of the DECK, mm. `CCMS!H16`. */
export function depthToTendons(input: PcdgInput, sections: PcdgSections): number {
  void sections;
  return (
    (input.section.height + input.section.slabThickness) * 1000 -
    input.prestress.centroidFromSoffit
  );
}

export interface PcdgMomentCapacity {
  neutralAxis: PcdgNeutralAxis;
  /** Tendon area after loss, mm2. */
  tendonArea: number;
  /** Depth to the tendons from the deck top, mm. */
  depth: number;
  /** Stress in the tendons at ultimate, MPa. `CCMS!C44` / `C46`. */
  tendonStress: number;
  /** Depth of the equivalent rectangular block, mm. `CCMS!D44` / `D46`. */
  blockDepth: number;
  /** Ultimate moment capacity at midspan, kN-m. `CCMS!H20`. */
  moment: number;
  /** Which branch produced it. */
  governedBy: 'slab' | 'girder';
}

/**
 * AASHTO's bonded-tendon flexural capacity, both branches.
 *
 * NO CAPACITY-REDUCTION FACTOR IS APPLIED, and that is not an omission here.
 * `CCMS!H11` holds Φ and is read by no formula in the workbook — correction C17.
 * Applying `profile.capacityFactor` would be an improvement this library is not
 * entitled to make silently, because it would lower every published PCDG rating
 * by ten per cent.
 */
export function pcdgMomentCapacity(
  input: PcdgInput,
  sections: PcdgSections,
): PcdgMomentCapacity {
  const axis = pcdgNeutralAxis(input, sections);
  const aps = tendonArea(input);
  const d = depthToTendons(input, sections);
  const fu = input.prestress.ultimateStrength;
  const k = 2 * (1.04 - input.prestress.yieldStrength / fu);
  const ts = input.section.slabThickness * 1000;
  const beff = input.slabWidth * 1000;
  const bw = input.section.webWidth * 1000;

  if (axis.withinSlab) {
    const tendonStress = fu * (1 - (k * axis.cSlab) / d);
    const blockDepth = beta1(input.slabStrength) * axis.cSlab;
    return {
      neutralAxis: axis,
      tendonArea: aps,
      depth: d,
      tendonStress,
      blockDepth,
      moment: (aps * tendonStress * (d - blockDepth / 2)) / 1e6,
      governedBy: 'slab',
    };
  }

  const tendonStress = fu * (1 - (k * axis.c) / d);
  const blockDepth = axis.betaAverage * axis.c;
  const flangeSurplus = input.slabStrength * beff - input.girderStrength * bw;
  const moment =
    (aps * tendonStress * (d - blockDepth / 2) +
      0.85 * axis.betaAverage * flangeSurplus * ts * (blockDepth / 2 - ts / 2)) /
    1e6;

  return {
    neutralAxis: axis,
    tendonArea: aps,
    depth: d,
    tendonStress,
    blockDepth,
    moment,
    governedBy: 'girder',
  };
}

/* --------------------------- shear capacity --------------------------- */

export interface PcdgShearCapacity {
  /** Stirrup area after section loss, mm2. */
  stirrupArea: number;
  /** The reduction on the concrete's contribution from web cracking. */
  crackReduction: number;
  /** Concrete's contribution Vc, kN. `CCMS!I32`. */
  concrete: number;
  /** Stirrups' contribution Vs, kN. `CCMS!I33`. */
  steel: number;
  /** Factored shear capacity at the support, kN. `CCMS!I34`. */
  shear: number;
}

/**
 * `CCMS!I23:I34`.
 *
 * `0.05 sqrt(f'c)` where RCDG's LFM uses `0.167` — a prestressed web is credited
 * with a third of the concrete shear a reinforced one is, and the stirrups carry
 * the rest. The effective depth is the composite depth less 50 mm, written in
 * the sheet as `DFDLLL!H9 - 0.05` and not derived from the tendon position.
 */
export function pcdgShearCapacity(
  input: PcdgInput,
  profile: StandardsProfile,
): PcdgShearCapacity {
  const bw = input.section.webWidth * 1000;
  const bar = (input.stirrupDiameter * input.stirrupDiameter * Math.PI) / 4;
  const stirrupArea = (1 - input.stirrupSectionLoss / 100) * bar;
  const reduction =
    input.shearCrackDepth / bw >= 0.5 ? 0 : 1 - input.shearCrackDepth / bw;
  /* Metres, and the 0.05 is the sheet's own. */
  const d = input.section.height + input.section.slabThickness - 0.05;

  const concrete = 0.05 * Math.sqrt(input.girderStrength) * bw * d * reduction;
  /* Two legs, two bars — `2 * 2 * Av`, as `CCMS!I33` writes it. */
  const steel = (2 * 2 * stirrupArea * input.rebarYield * d) / input.stirrupSpacing;

  return {
    stirrupArea,
    crackReduction: reduction,
    concrete,
    steel,
    shear: profile.shearCapacityFactor * (concrete + steel),
  };
}

/* --------------------------- the whole rating --------------------------- */

export interface PcdgRating {
  family: 'PCDG';
  continuity: 'simple';
  position: GirderPosition;
  sections: PcdgSections;
  distribution: Distribution;
  dead: PcdgDeadLoad;
  stresses: PcdgStresses;
  live: readonly LiveLoadDemand[];
  moment: PcdgMomentCapacity;
  shearCapacity: PcdgShearCapacity;
  result: RatingResult;
}

/** The serviceability checks carry no inventory/operating split — see `Check.levels`. */
const SERVICEABILITY_LEVELS: readonly RatingLevel[] = ['inventory'];
const UNIT_FACTORS = { dead: 1, live: { inventory: 1, operating: 1 } } as const;
/** `LRC!G15` halves the permanent stress before subtracting it. */
const HALF_DEAD_FACTORS = { dead: 0.5, live: { inventory: 1, operating: 1 } } as const;

/**
 * Rate a simple-span prestressed I-girder.
 *
 * SEVEN RATINGS, not four: three serviceability limits on the concrete fibres
 * and two strength limits each on moment and shear. On the reference interior
 * girder the governing value is the allowable TENSION at 1.2516 — below the
 * strength moment rating of 1.3154 that a transcription of RCDG's shape would
 * have reported as the answer.
 */
export function ratePcdgSimple(input: PcdgInput, profile: StandardsProfile): PcdgRating {
  /* Refused rather than ignored, as RCDG refuses the mirror case: a sidewalk on
     an interior girder would take asphalt off a bay that carries it, and an
     exterior girder's slab width is the one dimension that cannot be defaulted
     from the spacing. */
  const sidewalk = input.sidewalkWidth ?? null;
  if (input.position === 'interior' && sidewalk !== null) {
    throw new Error('An interior girder carries no sidewalk.');
  }

  const sections = pcdgSectionProperties(input.section, input.slabWidth);
  const dead = pcdgDeadLoad(input, sections);
  const stresses = pcdgStresses(input, sections, dead);

  const distribution = girderDistribution(
    input.girderSpacing,
    input.position === 'interior' ? 'interior-girder' : 'exterior-girder',
    profile.exteriorDf,
  );
  const live = allVehicles(profile).map((v) => liveLoadDemand(v.id, input.span, distribution));

  const moment = pcdgMomentCapacity(input, sections);
  const shearCapacity = pcdgShearCapacity(input, profile);

  const liveMoments: Partial<Record<VehicleId, number>> = {};
  const liveShears: Partial<Record<VehicleId, number>> = {};
  const liveTop: Partial<Record<VehicleId, number>> = {};
  const liveBottom: Partial<Record<VehicleId, number>> = {};
  for (const demand of live) {
    liveMoments[demand.vehicle] = demand.moment;
    liveShears[demand.vehicle] = demand.shear;
    const stress = liveStress(demand.moment, sections);
    liveTop[demand.vehicle] = stress.top;
    liveBottom[demand.vehicle] = stress.bottom;
  }

  const fc = input.girderStrength;
  const permanent = stresses.permanent;

  const checks: Check[] = [
    /* --- serviceability, `LRC!G14`, `G15`, `G21` --- */
    {
      id: 'sls-compression-all',
      label: 'Compression, all loads (midspan)',
      method: 'ASM',
      capacity: { inventory: 0.6 * fc, operating: 0.6 * fc },
      dead: permanent.top,
      live: liveTop,
      factors: UNIT_FACTORS,
      levels: SERVICEABILITY_LEVELS,
      governedBy: 'allowable compression 0.6 f’c',
    },
    {
      id: 'sls-compression-live',
      label: 'Compression, live load (midspan)',
      method: 'ASM',
      capacity: { inventory: 0.4 * fc, operating: 0.4 * fc },
      dead: permanent.top,
      live: liveTop,
      factors: HALF_DEAD_FACTORS,
      levels: SERVICEABILITY_LEVELS,
      governedBy: 'allowable compression 0.4 f’c on half the permanent stress',
    },
    {
      /* The capacity is NEGATIVE and so is the live-load stress: a tension limit
         at the soffit, compared against fibres this file signs as compression-
         positive. The rating equation divides one by the other and comes out
         positive; inverting the sign convention would invert this rating and
         nothing else. */
      id: 'sls-tension',
      label: 'Tension at soffit (midspan)',
      method: 'ASM',
      capacity: { inventory: -0.5 * Math.sqrt(fc), operating: -0.5 * Math.sqrt(fc) },
      dead: permanent.bottom,
      live: liveBottom,
      factors: UNIT_FACTORS,
      levels: SERVICEABILITY_LEVELS,
      governedBy: 'allowable tension 0.5 √f’c',
    },

    /* --- strength, `LRC!G37:J37` --- */
    {
      id: 'moment-midspan',
      label: 'Moment (midspan)',
      method: 'LFM',
      capacity: { inventory: moment.moment, operating: moment.moment },
      dead: dead.deadMoment + dead.superimposedMoment,
      live: liveMoments,
      governedBy: `${moment.governedBy}-controlled compression block`,
    },
    {
      id: 'shear-support',
      label: 'Shear (support)',
      method: 'LFM',
      capacity: { inventory: shearCapacity.shear, operating: shearCapacity.shear },
      dead: dead.deadShear + dead.superimposedShear,
      live: liveShears,
      ...(shearCapacity.crackReduction < 1
        ? {
            governedBy: `web cracking, concrete reduced to ${Math.round(
              shearCapacity.crackReduction * 100,
            )}%`,
          }
        : {}),
    },
  ];

  return {
    family: 'PCDG',
    continuity: 'simple',
    position: input.position,
    sections,
    distribution,
    dead,
    stresses,
    live,
    moment,
    shearCapacity,
    result: rate(checks, profile),
  };
}
